/**
 * THE lead-time scale — the one definition of what a user may pick and what
 * integer it stores.
 *
 * ── WHY AN INTEGER, AND WHY A DROPDOWN ───────────────────────────────────────
 * Product owner, 2026-08-08: *"should we make it a drop down with 1 to 5 days
 * then list weeks up to 25 with 25 being >25 so the sure picks rather than
 * typing and us having to format what they type?"*
 *
 * 🔑 **The value stored is `orders.lead_time_days`, an INTEGER NUMBER OF DAYS,
 * and that is the whole point of the change rather than a detail of it.** The
 * old control was a six-preset `<select>` plus a free-text "Custom…" box per
 * LINE ITEM, stored as TEXT (`order_items.lead_time`), and every consumer had
 * to run it back through `leadTimeToDays` — a three-regex parser with presets,
 * a bare-letter shorthand, and a range rule. **For anything created from here
 * on there is nothing to parse, so there is no unparseable case and no
 * fail-open.** The parser and the TEXT column stay for the legacy rows; they
 * are not replaced, and `server/helpers/leadTimeHelpers.js` still owns them.
 *
 * ── 🔴 WHAT THIS SCALE CANNOT SAY. FLAGGED, NOT SILENTLY WIDENED. ───────────
 * His *"1 to 5 days then … weeks"* is followed verbatim and is deliberately
 * NOT extended to cover these — widening a product owner's scale without
 * asking is how a control stops being the one he designed.
 *
 *   1. **6 days, and 8 to 13 days, are unreachable.** After `5 days` the next
 *      option is `1 week` (7) and then `2 weeks` (14). Anything in between has
 *      to round to one of them.
 *   2. **Nothing above 25 weeks can be stated exactly** — see the note on
 *      `LEAD_TIME_OPEN_ENDED_DAYS` below.
 *
 * ⚠️ **Do not "fix" either by editing this file.** They are recorded so that
 * the next reader knows they are known. Both are GAPS BETWEEN STATED POINTS,
 * and inventing intermediate options is a different act from restoring one
 * that already existed — which is the distinction the next block turns on.
 *
 * ── ✅ "SAME DAY" IS BACK IN, AND THE REASONING IS THE PART TO KEEP ──────────
 * A literal reading of *"1 to 5 days"* starts the scale at 1 and so drops
 * `Same Day`, which the legacy preset list opens with and which
 * `leadTimeToDays('Same Day')` maps to `0`. That was raised as a REMOVAL
 * rather than a gap and ruled back in on 2026-08-08:
 *
 *   - **It exists today and users can pick it.** Silently deleting something
 *     people can currently express is the change nobody asks for and everybody
 *     notices.
 *   - 🔑 **`0` is already a first-class value everywhere else in this feature,
 *     so a scale that could not produce it would contradict the rest of the
 *     design.** The red band in `components/LeadTimeRisk.js` is `slack <= 2`,
 *     so a zero-day lead time sits inside it; the slack SORT added the same day
 *     goes out of its way to keep `0` meaningful rather than blank; and
 *     `leadTimeToDays` was changed to return `null` instead of `0` for a parse
 *     failure precisely so that `0` could keep meaning "Same Day".
 *   - It is ordinary in this domain — someone driving to a supplier for a part
 *     that shoots tomorrow — not an exotic case.
 *
 * His sentence was describing the SHAPE of the control, not auditing the
 * options it replaces. **If he strikes it, delete the one entry; do not take
 * the reasoning above with it, because the same argument will apply the next
 * time a scale is rewritten.**
 */

/**
 * Days 0-5 — the fine end of the scale, where a single day genuinely matters.
 * `0` is `Same day`; see the block above for why it is here and not dropped.
 *
 * 🪤 **`0` IS A VALUE AND MUST SURVIVE EVERY FALSY TEST IT MEETS.** Anything
 * written against this list — a `value || fallback`, a `if (!leadTimeDays)`, a
 * `Number(x) || null` — silently converts "Same day" into "not set". Use
 * `== null` / `Number.isFinite`, exactly as the slack sort and
 * `leadTimeToDays` both had to.
 */
const DAY_VALUES = [0, 1, 2, 3, 4, 5];

/**
 * Weeks 1-24 as days. 25 is deliberately absent from this list because the
 * twenty-fifth entry is the open-ended one below — that is his
 * *"up to 25 with 25 being >25"*, not a 25-week option plus a separate
 * overflow.
 */
const WEEK_VALUES = Array.from({ length: 24 }, (_, i) => (i + 1) * 7);

/**
 * The top of the scale: **175 days = 25 weeks**, labelled `25+ weeks`.
 *
 * 🔑 **A REAL NUMBER, because the alternative is a sentinel** — and this
 * codebase has already been bitten once by a lead-time value that meant two
 * things at once (`leadTimeToDays` used to return `0` for "could not parse
 * this", which is also exactly what "Same Day" means). A magic 9999 here would
 * be the same mistake wearing a bigger number: it would arrive in
 * `computeSlackDays` as arithmetic and produce a slack of about minus ten
 * thousand days.
 *
 * ⚠️ **IT IS THE OPTIMISTIC END OF AN OPEN-ENDED RANGE, AND THAT IS AN
 * ASYMMETRY WORTH KNOWING BEFORE YOU TRUST A PILL.** An order set to
 * `25+ weeks` may take longer than 175 days, so its slack is a BEST case and
 * the real risk can only be worse than shown. That cuts against the house rule
 * one file away — `leadTimeToDays` maps "2-3 Days" to **3**, the UPPER bound,
 * with a docblock reading *"never guess in the optimistic direction"* — but
 * that rule is about a range with a stated top, and this range has none. 175 is
 * the only figure the user's own statement supports; anything larger would be
 * invented. The `+` in the label is what carries the rest of the meaning, which
 * is why the label must never be shortened to "25 weeks".
 *
 * 💡 In practice the optimism barely bites: at 175 days the slack is deeply
 * negative against any realistic needed-by date whichever number is stored, so
 * the pill reads red either way. **The choice matters far less than the fact
 * that it is written down** — which is the reason this note exists at all.
 */
const LEAD_TIME_OPEN_ENDED_DAYS = 175;
const LEAD_TIME_OPEN_ENDED_LABEL = '25+ weeks';

/**
 * ⚠️ `Same day`, in SENTENCE case, deliberately unlike the legacy preset's
 * `Same Day`. Every other label on this scale is `1 day` / `2 weeks`, and a
 * lone Title Case entry at the top of the list reads as a different KIND of
 * option rather than the first one. The legacy TEXT value keeps its own
 * spelling — `leadTimeToDays` matches on a lowercased string, so nothing
 * downstream can tell the difference.
 */
const dayLabel = (d) => (d === 0 ? 'Same day' : `${d} ${d === 1 ? 'day' : 'days'}`);
const weekLabel = (d) => {
  const w = d / 7;
  return `${w} ${w === 1 ? 'week' : 'weeks'}`;
};

/**
 * THE option list, in scale order. `{ value: <integer days>, label: <string> }`,
 * the same shape as `urgencyOptions` so it drops
 * into a plain `<select>` without an adapter.
 *
 * 🪤 **Never build a second copy of this by hand, and never filter it down per
 * caller.** The stored integer is meaningless without the list that produced
 * it: a value absent from here renders through `formatLeadTimeDays` as a bare
 * day count, which is correct but is not the wording the user picked.
 */
export const LEAD_TIME_OPTIONS = [
  ...DAY_VALUES.map((d) => ({ value: d, label: dayLabel(d) })),
  ...WEEK_VALUES.map((d) => ({ value: d, label: weekLabel(d) })),
  { value: LEAD_TIME_OPEN_ENDED_DAYS, label: LEAD_TIME_OPEN_ENDED_LABEL },
];

/**
 * The largest value the scale can produce. Exported for the server-side range
 * check to be stated against something rather than a magic literal, and for
 * tests. ⚠️ It is the OPEN-ENDED entry, so a stored value equal to it means
 * "at least this", not "exactly this".
 */
export const LEAD_TIME_MAX_DAYS = LEAD_TIME_OPEN_ENDED_DAYS;

/** Every integer the dropdown can store, for validation and for tests. */
export const LEAD_TIME_VALUES = LEAD_TIME_OPTIONS.map((o) => o.value);

/**
 * The order-level lead time a NEW order opens on.
 *
 * ── THE RULING, AND THE REASONING IS THE PART TO KEEP ────────────────────────
 * Product owner, 2026-08-09: *"default lead time should be set to 3 days as its
 * ther orderes responsability to state if its longer. I dont want to nag the
 * buyer"*.
 *
 * 🔑 **THE DIRECTION IS DELIBERATE AND IS THE OPPOSITE OF THE ONE A READER
 * REACHES FOR FIRST — do not re-raise this as a safety improvement.** Slack is
 * `daysUntilNeeded - leadDays`, so a LONGER default yields LESS slack and warns
 * EARLIER; "if the default is wrong, err toward nagging early" is therefore the
 * obvious argument, and it is the argument that was put to him. **He ruled
 * against it explicitly:** the person raising the order owns stating a longer
 * lead time, and a default that over-warns trains buyers to ignore the signal,
 * which costs more than the early warning buys. `5 days` and `1 week` were the
 * alternatives on the table; 3 days is his call, not a midpoint.
 *
 * 🟢 **AND IT MOVES NO ARITHMETIC, WHICH IS WHY IT IS SAFE AS WELL AS RULED.**
 * `createEmptyItem()` (`utils/orderFormHelpers.js`) opens every new line item on
 * the string `'Unknown'`, and `LEAD_TIME_PRESETS`
 * (`server/helpers/leadTimeHelpers.js`) maps `'unknown'` to **3 days** — so 3 is
 * already exactly what a blank new order behaves as today. **The defect this
 * closes was never the VALUE; it was that the value was invisible and unstated**,
 * inferred from a per-item sentinel whose control increment 3 deleted. Stating
 * it changes nothing about the number and everything about whether anyone can
 * see or change it. Pinned by `components/OrderForm.leadTime.test.js` §7.
 *
 * 🪤 **IT MUST BE A MEMBER OF THE SCALE, AND THAT IS ASSERTED RATHER THAN
 * ASSUMED** (§7). A default that is not an option would render through
 * `formatLeadTimeDays` as a bare day count and — worse — could not be re-selected
 * once the user changed away from it, so the form would silently lose the ability
 * to express its own default.
 *
 * ⚠️ **It is a DEFAULT, not a REMEMBERED value.** Every new order opens on it,
 * including each order in a "Create Another" run, so a buyer raising six
 * long-lead orders re-picks on each one. Carrying the previous order's choice
 * forward was considered and DECLINED — the reasoning is at `buildInitialForm`
 * in `components/OrderForm.js`, and it is a product question if it is ever
 * revisited, not a tidy-up.
 */
export const DEFAULT_NEW_ORDER_LEAD_TIME_DAYS = 3;

/**
 * Render a stored `lead_time_days` for display.
 *
 * 🔑 **TOTAL over the integers, not a lookup that can miss.** It answers for
 * values that are not on the scale as well as those that are — a hand-written
 * row, a value from a future scale change, or a legacy order whose TEXT lead
 * time was converted — because a `Map.get` returning `undefined` would render
 * an empty cell that reads as "no lead time" rather than "a lead time we did
 * not recognise". Those are different facts and the UI must not confuse them.
 *
 * `null` / `undefined` return `null`, so a caller can distinguish "not set"
 * from any value at all and render its own placeholder.
 */
export function formatLeadTimeDays(days) {
  if (days === null || days === undefined || days === '') return null;
  const n = Number(days);
  if (!Number.isFinite(n)) return null;
  if (n === LEAD_TIME_OPEN_ENDED_DAYS) return LEAD_TIME_OPEN_ENDED_LABEL;
  if (n % 7 === 0 && n >= 7) return weekLabel(n);
  // `dayLabel` owns the `0` → "Same day" case, so there is ONE spelling of it.
  return dayLabel(n);
}
